import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Table, Alert, Spinner, Row, Col, Card, CardHeader, CardBody, Pagination } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { searchDatabase, searchAll, getProductInfo, getOrderPositions, getOrdersByLedBatch } from './services/queries';

function Search() {
  const { productCode: urlProductCode } = useParams();
  const [productCode, setProductCode] = useState('');
  const [data, setData] = useState([]);
  const [matchedBatches, setMatchedBatches] = useState([]);
  const [inputType, setInputType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [useTestEndpoint] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [productInfo, setProductInfo] = useState(null);
  const [commonFields, setCommonFields] = useState(null);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [tileModules, setTileModules] = useState([]);

  useEffect(() => {
    // Only fetch data if productCode is present in the URL
    if (urlProductCode) {
      setProductCode(urlProductCode);
      fetchData(urlProductCode, 1, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProductCode]);

  const fetchData = async (code, page = 1, pageSizeParam = null) => {
    setIsLoading(true);
    setError('');
    try {
      const currentPageSize = pageSizeParam || pageSize;

      // Use universal search to try all types
      const jsonData = await searchAll(code, page, currentPageSize);

      // Extract search type from results
      const type = jsonData.searchType;

      // Handle paginated vs non-paginated responses
      const results = (jsonData.data !== undefined) ? jsonData.data : jsonData;
      const paginationInfo = jsonData.pagination || null;

      // Update inputType based on search results
      if (type === 'order_num') {
        setInputType('Order Number');
      } else if (type === 'tile_sn') {
        setInputType('Tile SN');
      } else if (type === 'module_sn') {
        setInputType('Module SN');
      } else {
        setInputType(null);
      }

      if (results.length === 0) {
        setError('No results found. The query was not found in order numbers, tile serials, or module serials.');
        setData([]);
        setMatchedBatches([]);
        setPagination(null);
        setProductInfo(null);
        setCommonFields(null);
        setUniqueFields([]);
        setAllPositions([]);
        setTileModules([]);
      } else {
        setData(results);
        setPagination(paginationInfo);
        setCurrentPage(page);
        
        // Extract common and unique fields
        if (results && results.length > 0) {
          const { common, unique } = extractCommonAndUniqueFields(results);
          setCommonFields(common);
          setUniqueFields(unique);
          
          // Fetch all positions for the order to calculate modules per tile accurately
          const firstResult = results[0];
          if (firstResult && firstResult.order_num) {
            fetchAllPositions(firstResult.order_num);
          }
          
          // For module_sn searches, fetch all modules for the tile to show complete panel layout
          if (type === 'module_sn' && firstResult && firstResult.tile_sn) {
            fetchTileModules(firstResult.tile_sn);
          } else if (type === 'tile_sn') {
            // For tile_sn searches, use the current results
            setTileModules(results);
          }
        } else {
          setCommonFields(null);
          setUniqueFields([]);
          setAllPositions([]);
          setTileModules([]);
        }
        
        // Fetch comprehensive product info for test endpoint
        if (useTestEndpoint) {
          fetchProductInfo(type, code);
        }
        
        // Only fetch LED matching for non-paginated results or first result
        if (results[0] && results[0].leds) {
          fetchOrdersByLeds(results[0].leds);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('An error occurred while fetching the data');
      setData([]);
      setMatchedBatches([]);
      setPagination(null);
      setProductInfo(null);
      setCommonFields(null);
      setUniqueFields([]);
      setAllPositions([]);
      setTileModules([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductInfo = async (type, code) => {
    try {
      // Use Supabase directly instead of API call
      const productData = await getProductInfo(type, code);
      setProductInfo(productData);
    } catch (error) {
      console.error('Failed to fetch product info:', error);
      setProductInfo(null);
    }
  };

  const fetchAllPositions = async (orderNum) => {
    try {
      // Use Supabase directly instead of API call
      const positionData = await getOrderPositions(orderNum);
      setAllPositions(positionData);
    } catch (error) {
      console.error('Failed to fetch all positions:', error);
      setAllPositions([]);
    }
  };

  const fetchTileModules = async (tileSn) => {
    try {
      // Use Supabase directly instead of API call
      const jsonData = await searchDatabase('tile_sn', tileSn, 1, 1000);

      // Handle paginated vs non-paginated responses
      const results = (jsonData.data !== undefined) ? jsonData.data : jsonData;
      setTileModules(results);
    } catch (error) {
      console.error('Failed to fetch tile modules:', error);
      setTileModules([]);
    }
  };

  const fetchOrdersByLeds = async (ledsValue) => {
    setIsLoadingBatches(true);
    setBatchError('');
    try {
        // Use Supabase directly instead of API call
        const data = await getOrdersByLedBatch(ledsValue);
        if (data.length === 0) {
            setBatchError('No matching LED batches found.');
        }
        setMatchedBatches(data);
    } catch (error) {
        console.error('Failed to fetch orders by LEDs', error);
        setBatchError('Error fetching matching LED batches.');
    } finally {
        setIsLoadingBatches(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productCode || productCode.trim() === '') {
      setError('Please enter a search query');
      return;
    }
    setCurrentPage(1);
    setPagination(null);
    setProductInfo(null);
    setCommonFields(null);
    setUniqueFields([]);
    setAllPositions([]);
    setTileModules([]);
    fetchData(productCode, 1, pageSize);
  };

  const handlePageChange = (newPage) => {
    fetchData(productCode, newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchData(productCode, 1, newPageSize);
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setProductCode(input);
  };

  // Helper function to calculate modules per tile based on L/R position values
  const calculateModulesPerTile = (data) => {
    // Use complete position data if available, otherwise fall back to paginated data
    const positionData = allPositions.length > 0 ? allPositions : data;
    
    if (!positionData || positionData.length === 0) return 'Unknown';
    
    // Extract all position values
    const positions = positionData.map(item => item.position).filter(pos => pos);
    
    if (positions.length === 0) return 'Unknown';
    
    
    // Look for L and R prefixed positions
    const lPositions = positions.filter(pos => pos.toString().startsWith('L'));
    const rPositions = positions.filter(pos => pos.toString().startsWith('R'));
    
    if (lPositions.length === 0 && rPositions.length === 0) {
      return 'Unknown (custom tile)';
    }
    
    // Extract the numbers after L and R (trim whitespace first)
    const lNumbers = lPositions.map(pos => {
      const match = pos.toString().trim().match(/^L(\d+)$/);
      return match ? parseInt(match[1]) : 0;
    });
    
    const rNumbers = rPositions.map(pos => {
      const match = pos.toString().trim().match(/^R(\d+)$/);
      return match ? parseInt(match[1]) : 0;
    });
    
    
    // Find the maximum L and R values
    const maxL = lNumbers.length > 0 ? Math.max(...lNumbers) : 0;
    const maxR = rNumbers.length > 0 ? Math.max(...rNumbers) : 0;
    
    
    // Check if both sides are equal (typical case)
    if (maxL > 0 && maxR > 0 && maxL === maxR) {
      return maxL * 2;
    }
    
    // If only one side exists, calculate based on that
    if (maxL > 0 && maxR === 0) {
      return maxL * 2;
    }
    
    if (maxR > 0 && maxL === 0) {
      return maxR * 2;
    }
    
    // If sides are unequal, show both
    if (maxL > 0 && maxR > 0 && maxL !== maxR) {
      return `${maxL + maxR} (L${maxL} + R${maxR})`;
    }
    
    return 'Unknown';
  };

  // Helper function to create LED panel visualization
  const createPanelVisualization = (data) => {
    // Use complete tile data if available, otherwise use current search results
    const moduleData = tileModules.length > 0 ? tileModules : data;
    
    if (!moduleData || moduleData.length === 0) return null;
    
    // Filter data to only include modules with valid L/R positions
    const modulesWithPositions = moduleData.filter(item => {
      const pos = item.position?.toString().trim();
      return pos && (pos.startsWith('L') || pos.startsWith('R'));
    });
    
    if (modulesWithPositions.length === 0) return null;
    
    // Parse positions and organize by L/R sides
    const leftModules = {};
    const rightModules = {};
    let maxPosition = 0;
    
    modulesWithPositions.forEach(item => {
      const pos = item.position.toString().trim();
      const lMatch = pos.match(/^L(\d+)$/);
      const rMatch = pos.match(/^R(\d+)$/);
      
      if (lMatch) {
        const position = parseInt(lMatch[1]);
        leftModules[position] = item.module_sn;
        maxPosition = Math.max(maxPosition, position);
      } else if (rMatch) {
        const position = parseInt(rMatch[1]);
        rightModules[position] = item.module_sn;
        maxPosition = Math.max(maxPosition, position);
      }
    });
    
    // Create the visualization grid
    const panelGrid = [];
    for (let i = 1; i <= maxPosition; i++) {
      panelGrid.push({
        position: i,
        leftModule: leftModules[i] || null,
        rightModule: rightModules[i] || null
      });
    }
    
    return panelGrid;
  };

  // Helper function to extract common and unique fields from data
  const extractCommonAndUniqueFields = (data) => {
    if (!data || data.length === 0) {
      return { common: null, unique: data };
    }

    if (data.length === 1) {
      // Single record - move common fields to product info
      const record = data[0];
      const commonFields = {
        firmware_version: record.firmware_version,
        product_family: record.product_family,
        leds: record.leds,
        r_nm: record.r_nm,
        r_mcd: record.r_mcd,
        r_v: record.r_v,
        g_nm: record.g_nm,
        g_mcd: record.g_mcd,
        g_v: record.g_v,
        b_nm: record.b_nm,
        b_mcd: record.b_mcd,
        b_v: record.b_v
      };
      
      const uniqueFields = [{
        order_num: record.order_num,
        tile_sn: record.tile_sn,
        module_sn: record.module_sn,
        position: record.position
      }];
      
      return { common: commonFields, unique: uniqueFields };
    }

    // Multiple records - check which fields are common
    const fieldsToCheck = ['firmware_version', 'product_family', 'leds', 'r_nm', 'r_mcd', 'r_v', 'g_nm', 'g_mcd', 'g_v', 'b_nm', 'b_mcd', 'b_v'];
    const commonFields = {};
    const uniqueFieldsKeys = ['order_num', 'tile_sn', 'module_sn', 'position'];
    
    fieldsToCheck.forEach(field => {
      const firstValue = data[0][field];
      const isCommon = data.every(record => record[field] === firstValue);
      if (isCommon) {
        commonFields[field] = firstValue;
      } else {
        // If not common, include in unique fields
        uniqueFieldsKeys.push(field);
      }
    });
    
    // Extract unique fields for table display
    const uniqueFields = data.map(record => {
      const uniqueRecord = {};
      uniqueFieldsKeys.forEach(field => {
        uniqueRecord[field] = record[field];
      });
      return uniqueRecord;
    });
    
    return { common: commonFields, unique: uniqueFields };
  };


  return (
    <Container>
      <Card className="mb-4">
        <CardHeader className="bg-dark text-white">
          <h5 className="mb-0">Search Products</h5>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label className="fw-bold">Search Database</Form.Label>
              <Form.Control
                type="text"
                value={productCode}
                onChange={handleInputChange}
                placeholder="Search for Order Number, Tile Serial, or Module Serial"
                className="form-control-lg"
                style={{fontSize: '1.1rem'}}
              />
              <Button
                variant="dark"
                type="submit"
                className="btn btn-dark mt-3 px-4 py-2"
                style={{fontSize: '1.1rem', fontWeight: '500'}}
              >
                Search
              </Button>
            </Form.Group>
          </Form>
        </CardBody>
      </Card>
        
        {/* Search Type Indicator - only shown after search results */}
        {inputType && data.length > 0 && (
          <Row className="mb-3">
            <Col lg={6}>
              <Card className="border-0 bg-light">
                <CardBody className="p-3">
                  <Alert variant="info" className="mb-0 p-2">
                    <small>
                      <strong>Search Type:</strong> {inputType}
                    </small>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        
      {isLoading ? (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </div>
    ) : error ? (
        <Alert variant="warning" className="mt-3">
          {error}
        </Alert>
      ) : data.length > 0 && (
        <>
        <Row className="mb-3">
          {/* Product Information Card */}
          <Col lg={(inputType === 'Tile SN' || inputType === 'Module SN') ? 8 : 12}>
            <Card className="h-100">
              <CardHeader className="bg-dark text-white">
                <h5 className="mb-0">Product Information</h5>
              </CardHeader>
              <CardBody>
                {/* PRIMARY INFORMATION - Most Important */}
                <div className="mb-3">
                  <Row>
                    <Col md={6}>
                      <div className="p-3 bg-light rounded border">
                        <h6 className="text-dark mb-2">Product Order Batch (POB)</h6>
                        <div className="h4 text-dark mb-0">
                          {useTestEndpoint && productInfo ? 
                            (productInfo.searchType === 'order_num' ? productInfo.searchValue : productInfo.orders.join(', ')) : 
                            (inputType === 'Order Number' ? productCode : data.length > 0 ? data[0].order_num : 'N/A')
                          }
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="p-3 bg-light rounded border">
                        <h6 className="text-dark mb-2">LED Batch</h6>
                        <div className="h4 text-dark mb-0">
                          {(useTestEndpoint && productInfo) ? 
                            (commonFields?.leds || 'N/A') : 
                            (commonFields?.leds || (data.length > 0 ? data[0].leds : 'N/A'))
                          }
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* SECONDARY INFORMATION - LED Properties */}
                {(commonFields?.r_nm || commonFields?.g_nm || commonFields?.b_nm) && (
                  <div className="mb-3">
                    <h6 className="text-dark mb-2">LED Properties</h6>
                    <Row>
                      {commonFields.r_nm && (
                        <Col md={4}>
                          <div className="p-2 border rounded text-center" style={{backgroundColor: '#ffe6e6'}}>
                            <div className="text-danger small">Red</div>
                            <div><strong>{commonFields.r_nm}nm</strong></div>
                            <div className="small">{commonFields.r_mcd}mcd • {commonFields.r_v}v</div>
                          </div>
                        </Col>
                      )}
                      {commonFields.g_nm && (
                        <Col md={4}>
                          <div className="p-2 border rounded text-center" style={{backgroundColor: '#e6ffe6'}}>
                            <div className="text-success small">Green</div>
                            <div><strong>{commonFields.g_nm}nm</strong></div>
                            <div className="small">{commonFields.g_mcd}mcd • {commonFields.g_v}v</div>
                          </div>
                        </Col>
                      )}
                      {commonFields.b_nm && (
                        <Col md={4}>
                          <div className="p-2 border rounded text-center" style={{backgroundColor: '#e6e6ff'}}>
                            <div className="text-primary small">Blue</div>
                            <div><strong>{commonFields.b_nm}nm</strong></div>
                            <div className="small">{commonFields.b_mcd}mcd • {commonFields.b_v}v</div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}

                {/* TERTIARY INFORMATION - Stats in Separate Cards */}
                <div className="mb-3">
                  <Row>
                    <Col md={4}>
                      <Card className="border">
                        <CardBody className="text-center p-3">
                          <div className="h5 text-dark mb-1">
                            {useTestEndpoint && productInfo ? productInfo.totalModules : data.length}
                          </div>
                          <div className="small text-muted">Total Modules</div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="border">
                        <CardBody className="text-center p-3">
                          <div className="h5 text-dark mb-1">
                            {useTestEndpoint && productInfo ? productInfo.totalTiles : 
                              (data.length > 0 ? new Set(data.map(item => item.tile_sn)).size : 0)}
                          </div>
                          <div className="small text-muted">Total Tiles</div>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="border">
                        <CardBody className="text-center p-3">
                          <div className="h5 text-dark mb-1">
                            {useTestEndpoint && productInfo ? productInfo.totalOrders : 1}
                          </div>
                          <div className="small text-muted">Orders</div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </div>

                {/* ADDITIONAL TECHNICAL DETAILS */}
                {(useTestEndpoint && productInfo) || (commonFields && (commonFields.firmware_version || commonFields.product_family)) || data.length > 0 ? (
                  <div className="border-top pt-3">
                    <h6 className="text-dark mb-2">Technical Details</h6>
                    <Row>
                      {(useTestEndpoint && productInfo?.tileTypes?.length > 0) && (
                        <Col md={6}>
                          <small className="text-muted">Tile Type:</small>
                          <div>{productInfo.tileTypes.join(', ')}</div>
                        </Col>
                      )}
                      {(useTestEndpoint && productInfo?.driverICs?.length > 0) && (
                        <Col md={6}>
                          <small className="text-muted">Driver IC:</small>
                          <div>{productInfo.driverICs.join(', ')}</div>
                        </Col>
                      )}
                      {(useTestEndpoint && productInfo?.controlSystems?.length > 0) && (
                        <Col md={6}>
                          <small className="text-muted">Control System:</small>
                          <div>{productInfo.controlSystems.join(', ')}</div>
                        </Col>
                      )}
                      {(commonFields?.firmware_version || productInfo?.firmware_version) && (
                        <Col md={6}>
                          <small className="text-muted">Firmware:</small>
                          <div>{commonFields?.firmware_version || productInfo?.firmware_version}</div>
                        </Col>
                      )}
                      {(commonFields?.product_family || productInfo?.product_family) && (
                        <Col md={6}>
                          <small className="text-muted">Product Family:</small>
                          <div>{commonFields?.product_family || productInfo?.product_family}</div>
                        </Col>
                      )}
                      {data.length > 0 && (
                        <Col md={6}>
                          <small className="text-muted">Modules per Tile:</small>
                          <div>{calculateModulesPerTile(data)}</div>
                        </Col>
                      )}
                    </Row>
                  </div>
                ) : null}
                
                {isLoadingBatches ? (
                  <div className="d-flex align-items-center mt-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Searching for matching LED batches...</span>
                  </div>
                ) : batchError ? (
                  <Alert variant="info" className="mt-2 mb-0">
                    {batchError}
                  </Alert>
                ) : matchedBatches.length > 1 && (
                  <div className="mt-3 border-top pt-3">
                    <h6 className="text-dark mb-2">Matching LED Batches</h6>
                    <p className="mb-2 text-muted">Other orders with identical LED properties:</p>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {matchedBatches.map((batch) => {
                        const orderNum = batch.order_num || batch.info1;
                        if (orderNum !== productCode) {
                          return (
                            <Link
                              key={orderNum}
                              to={`/search/${orderNum}`}
                              className="btn btn-outline-dark btn-sm text-decoration-none"
                              style={{
                                borderRadius: '20px',
                                padding: '4px 12px',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                            >
                              {orderNum}
                            </Link>
                          );
                        }
                        return null;
                      })}
                    </div>
                    <div className="alert alert-secondary p-2 mb-0">
                      <small>
                        <strong>Important:</strong> Matching LED batches don't guarantee identical visual performance 
                        due to variations in product design, thermal properties, driver chips, electronics design, and calibration.
                      </small>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          
          {/* LED Panel Layout Card - only for tile_sn and module_sn searches */}
          {(inputType === 'Tile SN' || inputType === 'Module SN') && data.length > 0 && (
            <Col lg={4}>
              <Card className="h-100">
                <CardHeader className="bg-dark text-white">
                  <h5 className="mb-0">Factory LED Panel Layout</h5>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-2">
                    <small className="text-muted">
                      <em>* Viewed from back of panel</em>
                    </small>
                  </div>
                  
                  {(() => {
                    const panelGrid = createPanelVisualization(data);
                    if (!panelGrid) {
                      return (
                        <div className="text-center text-muted">
                          <small>No L/R position data available for visualization</small>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="panel-visualization w-100">
                        {/* Header labels */}
                        <div className="d-flex mb-2">
                          <div className="position-header text-center flex-fill">
                            <strong>Left (L)</strong>
                          </div>
                          <div className="position-header text-center flex-fill">
                            <strong>Right (R)</strong>
                          </div>
                        </div>
                        
                        {/* Module grid */}
                        {panelGrid.map((row, index) => (
                          <div key={index} className="d-flex mb-2 align-items-center">
                            {/* Left module */}
                            <div 
                              className="module-slot text-center p-2 border rounded flex-fill me-1"
                              style={{
                                backgroundColor: row.leftModule ? '#e8f5e8' : '#f8f9fa',
                                borderColor: row.leftModule ? '#28a745' : '#dee2e6',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {row.leftModule ? (
                                <div className="fw-bold text-success">
                                  <div className="text-muted mb-1" style={{fontSize: '0.7rem'}}>L{row.position}</div>
                                  {row.leftModule}
                                </div>
                              ) : (
                                <div className="text-muted">-</div>
                              )}
                            </div>
                            
                            {/* Right module */}
                            <div 
                              className="module-slot text-center p-2 border rounded flex-fill ms-1"
                              style={{
                                backgroundColor: row.rightModule ? '#e8f5e8' : '#f8f9fa',
                                borderColor: row.rightModule ? '#28a745' : '#dee2e6',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {row.rightModule ? (
                                <div className="fw-bold text-success">
                                  <div className="text-muted mb-1" style={{fontSize: '0.7rem'}}>R{row.position}</div>
                                  {row.rightModule}
                                </div>
                              ) : (
                                <div className="text-muted">-</div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Footer note */}
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            Position 1 (top) → Position {panelGrid.length} (bottom)
                          </small>
                        </div>
                      </div>
                    );
                  })()}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
        <Card>
          <CardHeader className="bg-dark text-white">
            <h5 className="mb-0">Module Details</h5>
          </CardHeader>
          <CardBody className="p-0">
            <Table striped bordered hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  {Object.keys((uniqueFields && uniqueFields.length > 0) ? uniqueFields[0] : data[0]).map(key => (
                    <th key={key} className="text-center py-3">
                      <div className="fw-bold text-dark">
                        {key.replace('_', ' ').toUpperCase()}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {((uniqueFields && uniqueFields.length > 0) ? uniqueFields : data).map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-light' : ''}>
                    {Object.values(item).map((val, idx) => (
                      <td key={idx} className="text-center py-2">
                        <span className="fw-medium">{val}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
        
        {/* Pagination Controls */}
        {pagination && pagination.total > 10 && (
          <Card className="mt-3">
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col md={6}>
                  {/* Show page size selector only for non-tile searches or when there are multiple pages */}
                  {(inputType !== 'Tile SN' || pagination.totalPages > 1) ? (
                    <div className="d-flex align-items-center">
                      <span className="me-2 text-muted">Results per page:</span>
                      <select 
                        className="form-select form-select-sm" 
                        style={{width: 'auto'}}
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  ) : (
                    <div className="text-muted">
                      <small>All modules for this tile are shown</small>
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  <div className="text-end">
                    <small className="text-muted">
                      {inputType === 'Tile SN' && pagination.totalPages === 1 ? (
                        `${pagination.total} modules total`
                      ) : (
                        `Page ${currentPage} of ${pagination.totalPages} 
                        (${pagination.total} total records)`
                      )}
                    </small>
                  </div>
                </Col>
              </Row>
              {pagination.totalPages > 1 && (
                <Row>
                  <Col>
                    <Pagination className="justify-content-center mb-0">
                      <Pagination.First 
                        disabled={!pagination.hasPrev}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev 
                        disabled={!pagination.hasPrev}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      
                      {/* Show page numbers around current page */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const startPage = Math.max(1, currentPage - 2);
                        const pageNum = startPage + i;
                        if (pageNum <= pagination.totalPages) {
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      
                      <Pagination.Next 
                        disabled={!pagination.hasNext}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last 
                        disabled={!pagination.hasNext}
                        onClick={() => handlePageChange(pagination.totalPages)}
                      />
                    </Pagination>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        )}
        </>
      )}
    </Container>
  );
}

export default Search;
